import React, { useMemo } from "react";

import { LocalDateTime, ZoneId } from "@js-joda/core";
import { useGetRecentData } from "api/api-hooks/datasets-hooks";
import { ProgressSpinner } from "primereact/progressspinner";
import { T } from "services/lang/LanguageService";
import { isNumeric } from "utils/utils";
import SparkLineHandler from "./SparkLineHandler";
import { AdvancedProps, SparkLineProps, Usages } from "./SparkLineTypes";

export default function SparkLine(props: SparkLineProps) {
  const valueScaling = props.valueScaling || 1;
  const count = props.count || 14;
  const dataset = isNumeric(props.dataset)
    ? `${props.dataset}:CATEGORIZED_DATASET`
    : (props.dataset as string);

  const { data, isLoading } = useGetRecentData([dataset], {
    count,
    refTime:
      props.referenceTime &&
      LocalDateTime.parse(props.referenceTime).atZone(ZoneId.systemDefault()).toString(),
    options: {
      refetchInterval: props.updateFrequency ? props.updateFrequency * 1000 : 0,
      enabled: !!props.dataset,
    },
  });

  const sortedData = useMemo(() => {
    return data?.[0].data.sort((a, b) => a.key.localeCompare(b.key)) || [];
  }, [data]);
  
  const transformedData = useMemo((): {
    dataArray: number[];
    lastValueTime: null | Date;
  } => {
    if (!data?.[0]?.data?.length) return { dataArray: [], lastValueTime: null };

    const values = sortedData.map(d => d.value * valueScaling);
    const lastValueTime = new Date(sortedData[sortedData.length - 1].key);

    return { dataArray: values, lastValueTime };
  }, [data, sortedData, valueScaling]);

  const formattedData = useMemo(() => {
    return sortedData.map((d) => ({y: d.value * valueScaling, x: new Date(d.key)}));
  }, [sortedData, valueScaling]);

  if (isLoading) {
    return <ProgressSpinner />;
  }

  // props from legacy config
  const legacyProps = {
    // @ts-expect-error
    constant: props.constant,
    // @ts-expect-error
    dataset: props.baselineDataset,
    // @ts-expect-error
    usage: (props.useConstant ? "constant" : "dataset") as Usages,
    // @ts-expect-error
    color: props.baselineColor,
    // @ts-expect-error
    width: props.baselineWidth,
    // @ts-expect-error
    fillAboveColor: props.fillAboveColor,
    // @ts-expect-error
    fillBelowColor: props.fillBelowColor,
  };

  const advancedProps: AdvancedProps = {
    baseline: {
      ...legacyProps,
      ...props.baseline
    },
    data: formattedData,
    minLine: props.minLine,
    maxLine: props.maxLine,
    count,
    updateFrequency: props.updateFrequency,
    referenceTime: props.referenceTime,
    showYAxis: props.showYAxis,
  }

  return data?.length ? (
    <SparkLineHandler
      dataArray={transformedData.dataArray}
      lastValueTime={transformedData.lastValueTime}
      decimalPlaces={props.decimalPlaces}
      labelFontSize={props.labelFontSize}
      sparklineWidth={props.sparkLineWidth}
      lineColor={props.lineColor}
      lineWidth={props.lineWidth}
      unit={props.unit}
      maxAge={props.maxAge}
      dataset={dataset}
      title={data[0].title}
      height={props.height}
      overlayLabel={props.overlayLabel}
      vertical={props.vertical}
      horizontal={props.horizontal}
      advanced={props.advanced}
      advancedProps={advancedProps}
    />
  ) : (
    <div style={{ height: `${props.height ?? "auto"}px` }}>{T("word.no-records")}</div>
  );
}
